import { useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const FADE = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

function PlanCard({ plan, price, title, tagline, features, highlighted, ctaTestId, footnote }) {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [done, setDone] = useState(false);

    async function reserve(e) {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            await api.post("/orders/interest", { plan, email, name, quantity: 1 });
            setDone(true);
            toast.success(t("pricing.reserved_toast"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save your interest. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className={`relative rounded-[2rem] p-10 lg:p-12 ${highlighted
                ? "bg-gradient-to-br from-[#6B7DB8] to-[#4A5A95] text-white shadow-rem-card"
                : "bg-white border border-[#E5E1D8]/70 shadow-rem-soft"}`}
            data-testid={`plan-card-${plan}`}
        >
            {highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#F4D068] text-[#2D2B2A] text-xs font-sans font-semibold tracking-wider uppercase">
                    {t("pricing.bundle_badge")}
                </div>
            )}
            <div className="flex items-center gap-2 mb-5">
                <ForgetMeNotIcon size={22} />
                <p className={`font-sans text-[11px] uppercase tracking-[0.22em] font-semibold ${highlighted ? "text-white/85" : "text-[#6B7DB8]"}`}>
                    {tagline}
                </p>
            </div>
            <h3 className={`font-serif text-3xl lg:text-4xl mb-3 ${highlighted ? "text-white" : "text-[#2D2B2A]"}`}>{title}</h3>
            <div className="flex items-baseline gap-2 mb-1">
                <span className={`font-serif text-6xl tracking-tight ${highlighted ? "text-white" : "text-[#2D2B2A]"}`}>${price}</span>
                <span className={`text-sm ${highlighted ? "text-white/75" : "text-[#8A8786]"}`}>{t("pricing.price_suffix")}</span>
            </div>
            <p className={`text-sm mb-8 ${highlighted ? "text-white/75" : "text-[#8A8786]"}`}>{t("pricing.ownership_note")}</p>

            <ul className="space-y-3 mb-9">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                        <Check size={20} weight="bold" className={highlighted ? "text-[#F4D068] mt-0.5 flex-shrink-0" : "text-[#6B7DB8] mt-0.5 flex-shrink-0"} />
                        <span className={`text-sm leading-relaxed ${highlighted ? "text-white/95" : "text-[#5C5A59]"}`}>{f}</span>
                    </li>
                ))}
            </ul>

            {done ? (
                <div
                    className={`rounded-2xl p-5 text-sm font-medium ${highlighted ? "bg-white/15 text-white" : "bg-[#F4F6FB] text-[#4A5A95]"}`}
                    data-testid={`plan-reserved-${plan}`}
                >
                    {t("pricing.reserved")}
                </div>
            ) : (
                <form onSubmit={reserve} className="space-y-3" data-testid={`plan-form-${plan}`}>
                    <input
                        type="text"
                        placeholder={t("pricing.form_name_placeholder")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className={`w-full px-5 py-3 rounded-full text-sm outline-none transition-all ${
                            highlighted
                                ? "bg-white/15 text-white placeholder-white/60 border border-white/20 focus:bg-white/20"
                                : "bg-[#F4F6FB] text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB]"
                        }`}
                        data-testid={`plan-${plan}-name`}
                    />
                    <input
                        type="email"
                        required
                        placeholder={t("pricing.form_email_placeholder")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`w-full px-5 py-3 rounded-full text-sm outline-none transition-all ${
                            highlighted
                                ? "bg-white/15 text-white placeholder-white/60 border border-white/20 focus:bg-white/20"
                                : "bg-[#F4F6FB] text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB]"
                        }`}
                        data-testid={`plan-${plan}-email`}
                    />
                    <button
                        type="submit"
                        disabled={submitting}
                        className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm transition-all hover:-translate-y-0.5 disabled:opacity-60 ${
                            highlighted
                                ? "bg-[#F4D068] hover:bg-[#E6B844] text-[#2D2B2A]"
                                : "bg-[#6B7DB8] hover:bg-[#4A5A95] text-white"
                        }`}
                        data-testid={ctaTestId}
                    >
                        {submitting ? t("pricing.form_submitting") : t("pricing.form_submit")}
                        <ArrowRight weight="bold" size={16} />
                    </button>
                </form>
            )}
            {footnote && (
                <p className={`mt-6 text-xs italic leading-relaxed ${highlighted ? "text-white/70" : "text-[#8A8786]"}`} data-testid={`plan-footnote-${plan}`}>
                    {footnote}
                </p>
            )}
        </div>
    );
}

export function Pricing() {
    const { t } = useTranslation();
    const location = useLocation();
    const isKids = location.pathname.startsWith("/kids");
    const softwareFeatures = t("pricing.software_features", { returnObjects: true }) || [];
    const bundleFeatures = t("pricing.bundle_features", { returnObjects: true }) || [];
    const bundle17Features = t("pricing.bundle17_features", { returnObjects: true }) || [];

    return (
        <section className="relative py-32 px-6 bg-white overflow-hidden" data-testid="pricing-section" id="pricing">
            <FmnWatermark position="bottom-left" size={760} opacity={0.05} rotate={12} />
            <div className="max-w-6xl mx-auto">
                <motion.div {...FADE} className="text-center mb-16">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-6">
                        {t("pricing.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-tight max-w-3xl mx-auto">
                        {t("pricing.headline_part1")} <em className="italic text-[#4A5A95]">{t("pricing.headline_emphasis")}</em><br/>
                        {t("pricing.headline_part2")}
                    </h2>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                    <motion.div {...FADE}>
                        <PlanCard
                            plan="software"
                            price={120}
                            title={t("pricing.software_title")}
                            tagline={t("pricing.software_tagline")}
                            features={Array.isArray(softwareFeatures) ? softwareFeatures : []}
                            ctaTestId="reserve-software-btn"
                            footnote={t("pricing.byod_note")}
                        />
                    </motion.div>
                    <motion.div {...FADE} transition={{ ...FADE.transition, delay: 0.15 }}>
                        <PlanCard
                            plan="bundle"
                            price={180}
                            title={t("pricing.bundle_title")}
                            tagline={t("pricing.bundle_tagline")}
                            features={Array.isArray(bundleFeatures) ? bundleFeatures : []}
                            highlighted
                            ctaTestId="reserve-bundle-btn"
                        />
                    </motion.div>
                    <motion.div {...FADE} transition={{ ...FADE.transition, delay: 0.3 }}>
                        <PlanCard
                            plan="bundle17"
                            price={220}
                            title={t("pricing.bundle17_title")}
                            tagline={t("pricing.bundle17_tagline")}
                            features={Array.isArray(bundle17Features) ? bundle17Features : []}
                            ctaTestId="reserve-bundle17-btn"
                        />
                    </motion.div>
                </div>

                <motion.p {...FADE} transition={{ ...FADE.transition, delay: 0.2 }} className="mt-8 text-center text-xs italic text-[#8A8786]" data-testid="pricing-indicative-note">
                    {t("pricing.indicative_note")}
                </motion.p>

                <motion.p {...FADE} transition={{ ...FADE.transition, delay: 0.25 }} className="mt-10 text-center text-sm text-[#8A8786] max-w-2xl mx-auto" data-testid="pricing-charity-note">
                    {t(isKids ? "pricing.charity_note_kids" : "pricing.charity_note")}
                </motion.p>

            </div>
        </section>
    );
}
